import React from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb } from "lucide-react";
import Layout from "../components/layout";
import { AuthProviders } from "../components/auth";
import { useAuth } from "../hooks/useAuth";
import { BookCover } from "../components/common";
import { useTranslation } from "react-i18next";

const LoginPage: React.FC = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Redirect to dashboard if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleAuthSuccess = () => {
    // Navigation will happen automatically due to auth state change
    navigate("/dashboard");
  };

  const handleAuthCancel = () => {
    navigate("/");
  };

  const siteMetadata = {
    title: "Designing Multi-Agent Systems",
    description:
      "Access your book downloads, manage your account, and explore exclusive content for Designing Multi-Agent Systems",
    author: "Victor Dibia",
    image: "/images/og-service.png",
    keywords: "multi-agent, customer platform, dashboard, payments",
    twitterUsername: "@designingwithml",
  };

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <Layout meta={siteMetadata} title={t("auth.signIn")} link="/login">
        <main className="container mx-auto bg-primary min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-secondary">{t("auth.checkingAuth")}</p>
          </div>
        </main>
      </Layout>
    );
  }

  // Don't show login form if already authenticated
  if (isAuthenticated && user) {
    return null;
  }

  const LoginSteps = ({ className = "" }) => {
    const steps = [
      {
        number: 1,
        title: t("login.step1Title"),
        description: t("login.step1Desc"),
        fullDescription: t("login.step1FullDesc"),
      },
      {
        number: 2,
        title: t("login.step2Title"),
        description: t("login.step2Desc"),
        fullDescription: t("login.step2FullDesc"),
      },
      {
        number: 3,
        title: t("login.step3Title"),
        description: t("login.step3Desc"),
        fullDescription: t("login.step3FullDesc"),
      },
    ];

    return (
      <div
        className={`bg-primary rounded-lg border border-secondary p-6 lg:p-8 ${className}`}
      >
        <h2 className="text-2xl font-semibold text-primary mb-6">
          {t("login.howToAccess")}
        </h2>
        <div className="space-y-6">
          {steps.map((step) => (
            <div key={step.number} className="flex items-start text-left">
              <div className="bg-secondary text-primary rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-4 mt-1 flex-shrink-0">
                {step.number}
              </div>
              <div>
                <h3 className="font-semibold text-primary mb-2">
                  {step.title}
                </h3>
                <p className="text-secondary">
                  <span className="lg:hidden">{step.description}</span>
                  <span className="hidden lg:inline">
                    {step.fullDescription}
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden lg:block mt-8 text-secondary">
          <p>
            {t("login.needHelp")}
            <a
              href="https://github.com/victordibia/designing-multiagent-systems/issues"
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline ml-1 inline"
            >
              {t("login.reachOutOnGitHub")}
            </a>
            .
          </p>
        </div>
      </div>
    );
  };

  return (
    <Layout meta={siteMetadata} title={t("auth.signIn")} link="/login">
      <main className="container mx-auto px-4 py-12">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary mb-2">{t("auth.signIn")}</h1>
            <p className="text-secondary">
              {t("auth.accessYourBook")}
            </p>
          </div>

          <div className="flex justify-center mb-6">
            <BookCover size="mobile" />
          </div>

          <AuthProviders
            onSuccess={handleAuthSuccess}
            onCancel={handleAuthCancel}
            showCancel={true}
            inline={true}
          />

          <div className="mt-6">
            <LoginSteps />
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-secondary">
              {t("auth.dontHaveAccount")}{" "}
              <a
                href="/"
                className="text-accent hover:text-accent/80 font-medium"
              >
                {t("auth.browsePackages")}
              </a>
            </p>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="flex gap-8 items-start">
            {/* Left Side - Main Sign In */}
            <div className="flex-1">
              <div className="mb-8">
                <h1 className="text-5xl font-bold mb-3 text-primary">
                  {t("auth.signIn")}
                </h1>
                <p className="text-xl text-secondary mb-6">
                  {t("auth.accessYourBook")}
                </p>
              </div>

              {/* Main Auth Form - Wider */}
              <div className="bg-secondary border border-tertiary rounded-lg p-8 mb-6 max-w-lg">
                <AuthProviders
                  onSuccess={handleAuthSuccess}
                  onCancel={handleAuthCancel}
                  showCancel={false}
                  inline={true}
                  description={t("auth.signInToAccessDashboard")}
                />
              </div>

              <div className="bg-tertiary/20 border-l-4 border-accent p-4 rounded-r-lg mb-4 max-w-lg">
                <p className="text-secondary text-sm flex items-center">
                  <Lightbulb className="w-8 h-8 mr-2 text-accent" />
                  {t("auth.usePurchaseEmail")}
                </p>
              </div>

              <div className="text-sm text-secondary max-w-lg">
                <p>
                  {t("auth.dontHaveBook")}{" "}
                  <a href="/" className="text-accent hover:underline">
                    {t("auth.browsePackages")}
                  </a>{" "}
                  {t("auth.joinDevelopers")}
                </p>
              </div>
            </div>

            {/* Right Side - Book Cover & What's Included */}
            <div className="w-80 flex-shrink-0">
              <div className="sticky top-8">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    <BookCover size="desktop" width="w-80" />
                  </div>
                </div>

                {/* What's Included Box */}
                <div className="bg-secondary border border-tertiary rounded-lg p-6">
                  <h3 className="font-semibold text-primary mb-2">
                    {t("login.digitalLibraryIncludes")}
                  </h3>
                  <ul className="text-secondary text-sm space-y-2">
                    <li>• {t("login.pdfAndEpub")}</li>
                    <li>• {t("login.sampleCode")}</li>
                    <li>• {t("login.futureUpdates")}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default LoginPage;
